// src/components/Dashboard_ADM/GerenciamentoHomologacao.tsx
// UPDATED: 2026-07-20
// ✅ Conectado ao backend real (/homologation/all)
// ✅ Criar, publicar, arquivar, deletar
// ✅ Campos: title, type, licenseNumber, referenceNumber, description, content, coverImageUrl, visibility
// ✅ isPrivate — conteúdo protegido por senha (registroFepcssp do filiado/profissional)
// ✅ NOVO: modal de edição completo com todos os campos + upload de imagem/doc

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CheckSquare, Search, Plus, Eye, CheckCircle, XCircle,
  Clock, Loader2, AlertTriangle, RefreshCw, FileText, Upload,
  Globe, Building2, Archive, Trash2, Send, X as XIcon,
  Lock, Unlock, Pencil,
} from 'lucide-react';
import { API_BASE, getToken } from '../../utils/apiConfig';
import apiClient from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Homologation {
  id: string;
  title: string;
  description: string | null;
  content: string | null;
  coverImageUrl: string | null;
  documentUrl: string | null;
  type: string;
  status: 'draft' | 'published' | 'archived';
  visibility: 'all_affiliates' | 'specific' | 'public';
  referenceNumber: string | null;
  licenseNumber: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  publishedAt: string | null;
  downloadCount: number;
  viewCount: number;
  tags: string[] | null;
  isPrivate: boolean;
  affiliate?: { id: string; name: string } | null;
  createdAt?: string;
}

type EditForm = {
  title: string;
  type: string;
  visibility: 'all_affiliates' | 'specific' | 'public';
  licenseNumber: string;
  referenceNumber: string;
  description: string;
  content: string;
  coverImageUrl: string;
  documentUrl: string;
  issuedAt: string;
  expiresAt: string;
  isPrivate: boolean;
};

const TYPE_LABELS: Record<string, string> = {
  course_approval:   'Aprovação de Curso',
  license:           'Licença',
  certification:     'Certificação',
  official_document: 'Documento Oficial',
  credential:        'Credencial',
  regulation:        'Regulamento',
  other:             'Outros',
};

const STATUS_CFG = {
  draft:     { label: 'Rascunho',  bg: 'bg-gray-100',  text: 'text-gray-700',  border: 'border-gray-300',  Icon: Clock       },
  published: { label: 'Publicado', bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', Icon: CheckCircle },
  archived:  { label: 'Arquivado', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', Icon: Archive     },
} as const;

const VIS_CFG = {
  all_affiliates: { label: 'Todos Filiados', icon: Building2 },
  specific:       { label: 'Filiado Específico', icon: Building2 },
  public:         { label: 'Público',        icon: Globe     },
};

// ─── Form inicial ─────────────────────────────────────────────────────────────
const FORM_VAZIO = {
  title: '', type: 'official_document', visibility: 'public' as const,
  licenseNumber: '', referenceNumber: '', description: '', content: '',
  coverImageUrl: '', documentUrl: '', issuedAt: '', expiresAt: '',
  isPrivate: false,
};

// ─── Componente ───────────────────────────────────────────────────────────────
const GerenciamentoHomologacao: React.FC = () => {
  const [docs, setDocs]           = useState<Homologation[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [busca, setBusca]         = useState('');
  const [filtroStatus, setFiltro] = useState('todos');
  const [salvando, setSalvando]   = useState<string | null>(null);
  const [modalCriar, setModalCriar] = useState(false);
  const [form, setForm]           = useState(FORM_VAZIO);
  const [criando, setCriando]     = useState(false);
  const [detalhe, setDetalhe]     = useState<Homologation | null>(null);
  const [uploadandoImg, setUploadandoImg] = useState(false);
  const [imgPreview, setImgPreview]       = useState<string | null>(null);
  const [uploadandoDoc, setUploadandoDoc] = useState(false);
  const [docNome, setDocNome]             = useState<string | null>(null);

  // ── Estados do modal de edição ─────────────────────────────────────────────
  const [modalEditar, setModalEditar]         = useState(false);
  const [editando, setEditando]               = useState<Homologation | null>(null);
  const [editForm, setEditForm]               = useState<EditForm | null>(null);
  const [salvandoEdit, setSalvandoEdit]       = useState(false);
  const [uploadandoImgEdit, setUploadandoImgEdit] = useState(false);
  const [imgPreviewEdit, setImgPreviewEdit]       = useState<string | null>(null);
  const [uploadandoDocEdit, setUploadandoDocEdit] = useState(false);
  const [docNomeEdit, setDocNomeEdit]             = useState<string | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchDocs = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data: any = await apiClient.get('/homologation/all?limit=100');
      setDocs(data.documents ?? []);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar homologações');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     docs.length,
    draft:     docs.filter(d => d.status === 'draft').length,
    published: docs.filter(d => d.status === 'published').length,
    archived:  docs.filter(d => d.status === 'archived').length,
    privados:  docs.filter(d => d.isPrivate).length,
  }), [docs]);

  // ── Filtro ─────────────────────────────────────────────────────────────────
  const filtrados = useMemo(() => {
    return docs.filter(d => {
      if (filtroStatus !== 'todos' && d.status !== filtroStatus) return false;
      if (busca.trim()) {
        const q = busca.toLowerCase();
        return d.title.toLowerCase().includes(q) ||
               d.referenceNumber?.toLowerCase().includes(q) ||
               d.licenseNumber?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [docs, busca, filtroStatus]);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const handlePublicar = async (id: string) => {
    setSalvando(id);
    try {
      await apiClient.post(`/homologation/${id}/publish`, {});
      setDocs(prev => prev.map(d => d.id === id ? { ...d, status: 'published', publishedAt: new Date().toISOString() } : d));
    } catch (e: any) { alert(e?.response?.data?.message ?? 'Erro ao publicar'); }
    finally { setSalvando(null); }
  };

  const handleArquivar = async (id: string) => {
    if (!confirm('Arquivar esta homologação?')) return;
    setSalvando(id);
    try {
      await apiClient.post(`/homologation/${id}/archive`, {});
      setDocs(prev => prev.map(d => d.id === id ? { ...d, status: 'archived' } : d));
    } catch (e: any) { alert(e?.response?.data?.message ?? 'Erro ao arquivar'); }
    finally { setSalvando(null); }
  };

  const handleDeletar = async (id: string) => {
    if (!confirm('Deletar permanentemente? Esta ação não pode ser desfeita.')) return;
    setSalvando(id);
    try {
      await apiClient.delete(`/homologation/${id}`);
      setDocs(prev => prev.filter(d => d.id !== id));
    } catch (e: any) { alert(e?.response?.data?.message ?? 'Erro ao deletar'); }
    finally { setSalvando(null); }
  };

  // ── Abrir modal de edição ──────────────────────────────────────────────────
  const handleAbrirEditar = (doc: Homologation) => {
    setEditando(doc);
    setEditForm({
      title:           doc.title,
      type:            doc.type,
      visibility:      doc.visibility,
      licenseNumber:   doc.licenseNumber   ?? '',
      referenceNumber: doc.referenceNumber ?? '',
      description:     doc.description     ?? '',
      content:         doc.content         ?? '',
      coverImageUrl:   doc.coverImageUrl   ?? '',
      documentUrl:     doc.documentUrl     ?? '',
      issuedAt:        doc.issuedAt        ? doc.issuedAt.slice(0, 10) : '',
      expiresAt:       doc.expiresAt       ? doc.expiresAt.slice(0, 10) : '',
      isPrivate:       doc.isPrivate       ?? false,
    });
    setImgPreviewEdit(doc.coverImageUrl ?? null);
    setDocNomeEdit(null);
    setModalEditar(true);
  };

  // ── Salvar edição ──────────────────────────────────────────────────────────
  const handleSalvarEdicao = async () => {
    if (!editando || !editForm) return;
    if (!editForm.title.trim()) { alert('Título é obrigatório'); return; }
    setSalvandoEdit(true);
    try {
      await apiClient.patch(`/homologation/${editando.id}`, {
        title:           editForm.title,
        type:            editForm.type,
        visibility:      editForm.visibility,
        licenseNumber:   editForm.licenseNumber   || null,
        referenceNumber: editForm.referenceNumber || null,
        description:     editForm.description     || null,
        content:         editForm.content         || null,
        coverImageUrl:   editForm.coverImageUrl   || null,
        documentUrl:     editForm.documentUrl     || null,
        issuedAt:        editForm.issuedAt        || null,
        expiresAt:       editForm.expiresAt       || null,
        isPrivate:       editForm.isPrivate,
      });
      await fetchDocs();
      setModalEditar(false);
      setEditando(null);
      setEditForm(null);
      setImgPreviewEdit(null);
      setDocNomeEdit(null);
    } catch (e: any) { alert(e?.response?.data?.message ?? 'Erro ao salvar'); }
    finally { setSalvandoEdit(false); }
  };

  // ── Upload imagem (edição) ─────────────────────────────────────────────────
  const handleUploadImagemEdit = async (file: File) => {
    if (!file.type.startsWith('image/')) { alert('Selecione apenas imagens'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Máximo 10MB'); return; }
    setUploadandoImgEdit(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'homologacoes');
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      const url: string = data.url ?? data.fileUrl ?? data.path ?? data.signedUrl ?? '';
      setEditForm(p => p ? ({ ...p, coverImageUrl: url }) : p);
      setImgPreviewEdit(url);
    } catch (e: any) {
      alert('Erro no upload: ' + (e.message ?? 'tente novamente'));
    } finally { setUploadandoImgEdit(false); }
  };

  // ── Upload documento (edição) ─────────────────────────────────────────────
  const handleUploadDocumentoEdit = async (file: File) => {
    const allowed = ['application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'];
    if (!allowed.includes(file.type)) { alert('Apenas PDF ou DOCX'); return; }
    if (file.size > 30 * 1024 * 1024) { alert('Máximo 30MB'); return; }
    setUploadandoDocEdit(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'homologacoes/docs');
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      const url: string = data.url ?? data.fileUrl ?? data.path ?? '';
      setEditForm(p => p ? ({ ...p, documentUrl: url }) : p);
      setDocNomeEdit(file.name);
    } catch (e: any) {
      alert('Erro no upload: ' + (e.message ?? 'tente novamente'));
    } finally { setUploadandoDocEdit(false); }
  };

  const handleCriar = async () => {
    if (!form.title.trim()) { alert('Título é obrigatório'); return; }
    setCriando(true);
    try {
      const payload: any = {
        title:           form.title,
        type:            form.type,
        visibility:      form.visibility,
        description:     form.description || null,
        content:         form.content || null,
        coverImageUrl:   form.coverImageUrl || null,
        documentUrl:     form.documentUrl || null,
        licenseNumber:   form.licenseNumber || null,
        referenceNumber: form.referenceNumber || null,
        issuedAt:        form.issuedAt || null,
        expiresAt:       form.expiresAt || null,
        isPrivate:       form.isPrivate,
      };
      const data: any = await apiClient.post('/homologation', payload);
      await apiClient.post(`/homologation/${data.id}/publish`, {});
      await fetchDocs();
      setModalCriar(false);
      setForm(FORM_VAZIO);
      setImgPreview(null);
      setDocNome(null);
      alert('Homologação publicada com sucesso!');
    } catch (e: any) { alert(e?.response?.data?.message ?? 'Erro ao criar'); }
    finally { setCriando(false); }
  };

  // ── Upload imagem de capa ─────────────────────────────────────────────────
  const handleUploadImagem = async (file: File) => {
    if (!file.type.startsWith('image/')) { alert('Selecione apenas imagens'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('Máximo 10MB'); return; }
    setUploadandoImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'homologacoes');
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      const url: string = data.url ?? data.fileUrl ?? data.path ?? data.signedUrl ?? '';
      setForm(p => ({ ...p, coverImageUrl: url }));
      setImgPreview(url);
    } catch (e: any) {
      alert('Erro no upload: ' + (e.message ?? 'tente novamente'));
    } finally { setUploadandoImg(false); }
  };

  const handleUploadDocumento = async (file: File) => {
    const allowed = ['application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'];
    if (!allowed.includes(file.type)) { alert('Apenas PDF ou DOCX'); return; }
    if (file.size > 30 * 1024 * 1024) { alert('Máximo 30MB'); return; }
    setUploadandoDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'homologacoes/docs');
      const res = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error('Falha no upload');
      const data = await res.json();
      const url: string = data.url ?? data.fileUrl ?? data.path ?? '';
      setForm(p => ({ ...p, documentUrl: url }));
      setDocNome(file.name);
    } catch (e: any) {
      alert('Erro no upload: ' + (e.message ?? 'tente novamente'));
    } finally { setUploadandoDoc(false); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <CheckSquare className="w-7 h-7 text-purple-600"/> Homologações
            </h2>
            <p className="text-gray-600">Gerencie documentos de homologação publicados para filiados e público</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchDocs} className="p-2.5 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50">
              <RefreshCw className="w-5 h-5"/>
            </button>
            <button onClick={() => setModalCriar(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700">
              <Plus className="w-5 h-5"/> Nova Homologação
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total',      value: stats.total,     bg: 'bg-gray-50',    border: 'border-gray-200'   },
            { label: 'Rascunhos',  value: stats.draft,     bg: 'bg-gray-50',    border: 'border-gray-200'   },
            { label: 'Publicados', value: stats.published, bg: 'bg-green-50',   border: 'border-green-200'  },
            { label: 'Arquivados', value: stats.archived,  bg: 'bg-amber-50',   border: 'border-amber-200'  },
            { label: 'Privados',   value: stats.privados,  bg: 'bg-purple-50',  border: 'border-purple-200' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4 border ${s.border}`}>
              <p className="text-sm text-gray-600 mb-1">{s.label}</p>
              <p className="text-2xl font-black text-gray-900">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
          <input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por título, licença ou número…"
            className="w-full pl-11 pr-4 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"/>
        </div>
        <select value={filtroStatus} onChange={e => setFiltro(e.target.value)}
          className="px-4 py-2.5 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700">
          <option value="todos">Todos</option>
          <option value="draft">Rascunhos</option>
          <option value="published">Publicados</option>
          <option value="archived">Arquivados</option>
        </select>
      </div>

      {/* LOADING / ERROR */}
      {loading && <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-purple-600 animate-spin"/></div>}
      {error   && <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 flex items-center gap-4"><AlertTriangle className="w-7 h-7 text-red-600"/><p className="text-red-800 font-semibold">{error}</p></div>}

      {/* LISTA */}
      {!loading && !error && (
        filtrados.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 py-20 text-center">
            <FileText className="w-14 h-14 text-gray-300 mx-auto mb-4"/>
            <p className="text-gray-600 font-semibold">Nenhuma homologação encontrada</p>
            <p className="text-gray-400 text-sm mt-1">{busca || filtroStatus !== 'todos' ? 'Ajuste os filtros' : 'Clique em "Nova Homologação" para começar'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtrados.map(doc => {
              const sc = STATUS_CFG[doc.status];
              const vc = VIS_CFG[doc.visibility];
              const VIcon = vc.icon;
              const isSaving = salvando === doc.id;
              return (
                <div key={doc.id} className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className={`h-1 w-full ${doc.status === 'published' ? 'bg-green-500' : doc.status === 'archived' ? 'bg-amber-400' : 'bg-gray-300'}`}/>
                  <div className="p-5 flex items-start gap-5">
                    {/* Imagem de capa */}
                    {doc.coverImageUrl ? (
                      <img src={doc.coverImageUrl} alt={doc.title}
                        className="w-20 h-20 rounded-xl object-cover border-2 border-gray-200 flex-shrink-0"/>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-8 h-8 text-gray-300"/>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h3 className="font-black text-gray-900 text-lg leading-tight">{doc.title}</h3>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {/* Badge Privado */}
                          {doc.isPrivate && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border bg-purple-100 text-purple-800 border-purple-300">
                              <Lock className="w-3 h-3"/> Privado
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>
                            <sc.Icon className="w-3.5 h-3.5"/>{sc.label}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500 mb-3">
                        <span>Tipo: <strong className="text-gray-700">{TYPE_LABELS[doc.type] ?? doc.type}</strong></span>
                        {doc.licenseNumber   && <span>Licença FEPCSSP: <strong className="text-gray-700">{doc.licenseNumber}</strong></span>}
                        {doc.referenceNumber && <span>Homologação FEPCSSP: <strong className="text-gray-700">{doc.referenceNumber}</strong></span>}
                        <span className="flex items-center gap-1"><VIcon className="w-3 h-3"/>{vc.label}</span>
                        <span>👁 {doc.viewCount}</span>
                      </div>

                      {doc.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{doc.description}</p>
                      )}

                      {/* Ações */}
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => setDetalhe(doc)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                          <Eye className="w-3.5 h-3.5"/> Ver
                        </button>
                        <button onClick={() => handleAbrirEditar(doc)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-50">
                          <Pencil className="w-3.5 h-3.5"/> Editar
                        </button>
                        {doc.status === 'draft' && (
                          <button onClick={() => handlePublicar(doc.id)} disabled={isSaving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 disabled:opacity-50">
                            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>} Publicar
                          </button>
                        )}
                        {doc.status === 'published' && (
                          <button onClick={() => handleArquivar(doc.id)} disabled={isSaving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 text-xs font-bold hover:bg-amber-50 disabled:opacity-50">
                            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Archive className="w-3.5 h-3.5"/>} Arquivar
                          </button>
                        )}
                        <button onClick={() => handleDeletar(doc.id)} disabled={isSaving}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 disabled:opacity-50">
                          <Trash2 className="w-3.5 h-3.5"/> Deletar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── MODAL CRIAR ───────────────────────────────────────────────────────── */}
      {modalCriar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalCriar(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-black text-gray-900">Publicar Homologação</h3>
              <button onClick={() => setModalCriar(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-4">

              {/* ── TOGGLE PRIVADO ── */}
              <div
                onClick={() => setForm(p => ({ ...p, isPrivate: !p.isPrivate }))}
                className={`flex items-center justify-between gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
                  ${form.isPrivate
                    ? 'border-purple-400 bg-purple-50'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
              >
                <div className="flex items-center gap-3">
                  {form.isPrivate
                    ? <Lock className="w-5 h-5 text-purple-600 flex-shrink-0"/>
                    : <Unlock className="w-5 h-5 text-gray-400 flex-shrink-0"/>}
                  <div>
                    <p className={`text-sm font-bold ${form.isPrivate ? 'text-purple-800' : 'text-gray-700'}`}>
                      {form.isPrivate ? 'Conteúdo Privado' : 'Conteúdo Público'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {form.isPrivate
                        ? 'Somente filiados/profissionais com registro FEPCSSP poderão acessar'
                        : 'Visível para todos no portal público'}
                    </p>
                  </div>
                </div>
                {/* Toggle pill */}
                <div className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.isPrivate ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isPrivate ? 'translate-x-5' : 'translate-x-0'}`}/>
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Título *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-900"
                  placeholder="Ex: Homologação de Curso — Cosmetologia Avançada"/>
              </div>

              {/* Tipo + Visibilidade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tipo</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900">
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Visibilidade</label>
                  <select value={form.visibility} onChange={e => setForm(p => ({ ...p, visibility: e.target.value as any }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900">
                    <option value="public">Público (portal)</option>
                    <option value="all_affiliates">Todos Filiados</option>
                    <option value="specific">Filiado Específico</option>
                  </select>
                </div>
              </div>

              {/* Licença + Homologação */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Licença FEPCSSP</label>
                  <input value={form.licenseNumber} onChange={e => setForm(p => ({ ...p, licenseNumber: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900" placeholder="Ex: 146031"/>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Homologação FEPCSSP</label>
                  <input value={form.referenceNumber} onChange={e => setForm(p => ({ ...p, referenceNumber: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900" placeholder="Ex: 013"/>
                </div>
              </div>

              {/* Resumo */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Resumo</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl resize-none text-gray-900"
                  placeholder="Breve descrição da homologação…"/>
              </div>

              {/* Conteúdo completo */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Conteúdo Completo</label>
                <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={5}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl resize-none text-gray-900"
                  placeholder="Texto completo da homologação, informações detalhadas…"/>
              </div>

              {/* Upload imagem de capa */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Imagem de Capa</label>
                {imgPreview || form.coverImageUrl ? (
                  <div className="relative">
                    <img src={imgPreview || form.coverImageUrl || ''} alt="Capa"
                      className="w-full h-40 object-cover rounded-xl border-2 border-gray-200"/>
                    <button type="button"
                      onClick={() => { setImgPreview(null); setForm(p => ({ ...p, coverImageUrl: '' })); }}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200">
                      <XIcon className="w-4 h-4"/>
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadandoImg ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30'}`}>
                    {uploadandoImg ? (
                      <><Loader2 className="w-8 h-8 text-purple-500 animate-spin"/><p className="text-sm text-purple-600 font-medium">Enviando…</p></>
                    ) : (
                      <><Upload className="w-8 h-8 text-gray-400"/><p className="text-sm text-gray-600 font-medium">Clique para selecionar a imagem</p><p className="text-xs text-gray-400">JPG, PNG, WEBP — máx. 10MB</p></>
                    )}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadImagem(f); }}
                      disabled={uploadandoImg}/>
                  </label>
                )}
              </div>

              {/* Upload documento PDF/DOCX */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Documento (PDF ou DOCX)</label>
                {form.documentUrl ? (
                  <div className="flex items-center gap-3 p-3 border-2 border-green-200 bg-green-50 rounded-xl">
                    <FileText className="w-5 h-5 text-green-600 flex-shrink-0"/>
                    <p className="text-sm font-semibold text-green-800 flex-1 truncate">{docNome ?? 'Documento anexado'}</p>
                    <button type="button"
                      onClick={() => { setForm(p => ({ ...p, documentUrl: '' })); setDocNome(null); }}
                      className="p-1 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 flex-shrink-0">
                      <XIcon className="w-4 h-4"/>
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadandoDoc ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30'}`}>
                    {uploadandoDoc ? (
                      <><Loader2 className="w-7 h-7 text-purple-500 animate-spin"/><p className="text-sm text-purple-600 font-medium">Enviando documento…</p></>
                    ) : (
                      <><Upload className="w-7 h-7 text-gray-400"/><p className="text-sm text-gray-600 font-medium">Clique para selecionar PDF ou DOCX</p><p className="text-xs text-gray-400">Máx. 30MB</p></>
                    )}
                    <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden" disabled={uploadandoDoc}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadDocumento(f); }}/>
                  </label>
                )}
              </div>

              {/* Datas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Data de Emissão</label>
                  <input type="date" value={form.issuedAt} onChange={e => setForm(p => ({ ...p, issuedAt: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900"/>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Data de Expiração</label>
                  <input type="date" value={form.expiresAt} onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900"/>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModalCriar(false)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleCriar} disabled={criando}
                className="flex-1 px-4 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {criando ? <Loader2 className="w-5 h-5 animate-spin"/> : <Send className="w-5 h-5"/>}
                Publicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL EDITAR ──────────────────────────────────────────────────────── */}
      {modalEditar && editForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalEditar(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-xl font-black text-gray-900">Editar Homologação</h3>
                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-md">{editando?.title}</p>
              </div>
              <button onClick={() => setModalEditar(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="p-6 space-y-4">

              {/* Toggle Privado */}
              <div
                onClick={() => setEditForm(p => p ? ({ ...p, isPrivate: !p.isPrivate }) : p)}
                className={`flex items-center justify-between gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all select-none
                  ${editForm.isPrivate ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
              >
                <div className="flex items-center gap-3">
                  {editForm.isPrivate
                    ? <Lock className="w-5 h-5 text-purple-600 flex-shrink-0"/>
                    : <Unlock className="w-5 h-5 text-gray-400 flex-shrink-0"/>}
                  <div>
                    <p className={`text-sm font-bold ${editForm.isPrivate ? 'text-purple-800' : 'text-gray-700'}`}>
                      {editForm.isPrivate ? 'Conteúdo Privado' : 'Conteúdo Público'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {editForm.isPrivate
                        ? 'Somente filiados/profissionais com registro FEPCSSP poderão acessar'
                        : 'Visível para todos no portal público'}
                    </p>
                  </div>
                </div>
                <div className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${editForm.isPrivate ? 'bg-purple-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${editForm.isPrivate ? 'translate-x-5' : 'translate-x-0'}`}/>
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Título *</label>
                <input value={editForm.title} onChange={e => setEditForm(p => p ? ({ ...p, title: e.target.value }) : p)}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-900"/>
              </div>

              {/* Tipo + Visibilidade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tipo</label>
                  <select value={editForm.type} onChange={e => setEditForm(p => p ? ({ ...p, type: e.target.value }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900">
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Visibilidade</label>
                  <select value={editForm.visibility} onChange={e => setEditForm(p => p ? ({ ...p, visibility: e.target.value as any }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900">
                    <option value="public">Público (portal)</option>
                    <option value="all_affiliates">Todos Filiados</option>
                    <option value="specific">Filiado Específico</option>
                  </select>
                </div>
              </div>

              {/* Licença + Homologação */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Licença FEPCSSP</label>
                  <input value={editForm.licenseNumber} onChange={e => setEditForm(p => p ? ({ ...p, licenseNumber: e.target.value }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900" placeholder="Ex: 146031"/>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Homologação FEPCSSP</label>
                  <input value={editForm.referenceNumber} onChange={e => setEditForm(p => p ? ({ ...p, referenceNumber: e.target.value }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900" placeholder="Ex: 013"/>
                </div>
              </div>

              {/* Resumo */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Resumo</label>
                <textarea value={editForm.description} onChange={e => setEditForm(p => p ? ({ ...p, description: e.target.value }) : p)} rows={3}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl resize-none text-gray-900"
                  placeholder="Breve descrição da homologação…"/>
              </div>

              {/* Conteúdo completo */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Conteúdo Completo</label>
                <textarea value={editForm.content} onChange={e => setEditForm(p => p ? ({ ...p, content: e.target.value }) : p)} rows={6}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl resize-none text-gray-900"
                  placeholder="Texto completo da homologação…"/>
              </div>

              {/* Imagem de capa */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Imagem de Capa</label>
                {imgPreviewEdit || editForm.coverImageUrl ? (
                  <div className="relative">
                    <img src={imgPreviewEdit || editForm.coverImageUrl || ''} alt="Capa"
                      className="w-full h-40 object-cover rounded-xl border-2 border-gray-200"/>
                    <button type="button"
                      onClick={() => { setImgPreviewEdit(null); setEditForm(p => p ? ({ ...p, coverImageUrl: '' }) : p); }}
                      className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200">
                      <XIcon className="w-4 h-4"/>
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadandoImgEdit ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30'}`}>
                    {uploadandoImgEdit ? (
                      <><Loader2 className="w-8 h-8 text-purple-500 animate-spin"/><p className="text-sm text-purple-600 font-medium">Enviando…</p></>
                    ) : (
                      <><Upload className="w-8 h-8 text-gray-400"/><p className="text-sm text-gray-600 font-medium">Clique para selecionar a imagem</p><p className="text-xs text-gray-400">JPG, PNG, WEBP — máx. 10MB</p></>
                    )}
                    <input type="file" accept="image/*" className="hidden" disabled={uploadandoImgEdit}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadImagemEdit(f); }}/>
                  </label>
                )}
              </div>

              {/* Documento PDF/DOCX */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Documento (PDF ou DOCX)</label>
                {editForm.documentUrl ? (
                  <div className="flex items-center gap-3 p-3 border-2 border-green-200 bg-green-50 rounded-xl">
                    <FileText className="w-5 h-5 text-green-600 flex-shrink-0"/>
                    <p className="text-sm font-semibold text-green-800 flex-1 truncate">{docNomeEdit ?? 'Documento anexado'}</p>
                    <button type="button"
                      onClick={() => { setEditForm(p => p ? ({ ...p, documentUrl: '' }) : p); setDocNomeEdit(null); }}
                      className="p-1 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 flex-shrink-0">
                      <XIcon className="w-4 h-4"/>
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploadandoDocEdit ? 'border-purple-300 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/30'}`}>
                    {uploadandoDocEdit ? (
                      <><Loader2 className="w-7 h-7 text-purple-500 animate-spin"/><p className="text-sm text-purple-600 font-medium">Enviando documento…</p></>
                    ) : (
                      <><Upload className="w-7 h-7 text-gray-400"/><p className="text-sm text-gray-600 font-medium">Clique para selecionar PDF ou DOCX</p><p className="text-xs text-gray-400">Máx. 30MB</p></>
                    )}
                    <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden" disabled={uploadandoDocEdit}
                      onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadDocumentoEdit(f); }}/>
                  </label>
                )}
              </div>

              {/* Datas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Data de Emissão</label>
                  <input type="date" value={editForm.issuedAt} onChange={e => setEditForm(p => p ? ({ ...p, issuedAt: e.target.value }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900"/>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Data de Expiração</label>
                  <input type="date" value={editForm.expiresAt} onChange={e => setEditForm(p => p ? ({ ...p, expiresAt: e.target.value }) : p)}
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-xl text-gray-900"/>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModalEditar(false)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleSalvarEdicao} disabled={salvandoEdit}
                className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvandoEdit ? <Loader2 className="w-5 h-5 animate-spin"/> : <Pencil className="w-5 h-5"/>}
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DETALHE ─────────────────────────────────────────────────────── */}
      {detalhe && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetalhe(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-black text-gray-900">{detalhe.title}</h3>
                {detalhe.isPrivate && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-300">
                    <Lock className="w-3 h-3"/> Privado
                  </span>
                )}
              </div>
              <button onClick={() => setDetalhe(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {detalhe.coverImageUrl && (
              <img src={detalhe.coverImageUrl} alt={detalhe.title} className="w-full h-52 object-cover"/>
            )}
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${STATUS_CFG[detalhe.status].bg} ${STATUS_CFG[detalhe.status].text} ${STATUS_CFG[detalhe.status].border}`}>
                  {STATUS_CFG[detalhe.status].label}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-300">
                  {TYPE_LABELS[detalhe.type] ?? detalhe.type}
                </span>
                {detalhe.isPrivate && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                    <Lock className="w-3 h-3"/> Acesso restrito — requer registro FEPCSSP
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Licença FEPCSSP',    value: detalhe.licenseNumber   },
                  { label: 'Homologação FEPCSSP', value: detalhe.referenceNumber },
                  { label: 'Emissão',             value: detalhe.issuedAt ? new Date(detalhe.issuedAt).toLocaleDateString('pt-BR') : null },
                  { label: 'Expiração',           value: detalhe.expiresAt ? new Date(detalhe.expiresAt).toLocaleDateString('pt-BR') : null },
                  { label: 'Publicado em',        value: detalhe.publishedAt ? new Date(detalhe.publishedAt).toLocaleDateString('pt-BR') : null },
                  { label: 'Visualizações',       value: String(detalhe.viewCount) },
                ].filter(f => f.value).map(f => (
                  <div key={f.label} className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">{f.label}</p>
                    <p className="font-bold text-gray-800 text-sm">{f.value}</p>
                  </div>
                ))}
              </div>
              {detalhe.description && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Resumo</p>
                  <p className="text-sm text-gray-700">{detalhe.description}</p>
                </div>
              )}
              {detalhe.content && (
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Conteúdo Completo</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{detalhe.content}</p>
                </div>
              )}
              {detalhe.documentUrl && (
                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Documento Anexado</p>
                  <div className="flex gap-2 flex-wrap">
                    <a href={detalhe.documentUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors">
                      <Eye className="w-4 h-4"/> Visualizar
                    </a>
                    <a href={detalhe.documentUrl} download
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 transition-colors">
                      <Upload className="w-4 h-4 rotate-180"/> Baixar
                    </a>
                  </div>
                  {detalhe.documentUrl.toLowerCase().includes('.pdf') && (
                    <iframe src={detalhe.documentUrl} className="w-full h-96 rounded-xl border border-gray-200" title="Documento"/>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200">
              <button onClick={() => setDetalhe(null)}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciamentoHomologacao;